import React from 'react';
import {
  HealthAndSafety as DiagnosticsIcon,
  Agriculture as HarvestIcon,
  Psychology as WorkItemsIcon,
  Security as ClearanceIcon,
  AdminPanelSettings as PimSyncIcon,
  Radar as ChangeRadarIcon,
  Policy as ConditionalAccessReadinessIcon,
  ManageSearch as ConditionalAccessInvestigationIcon,
  Hub as PartnerGovernanceIcon,
  Approval as ApprovalIcon,
  Assessment as ExceptionReportIcon,
  Insights as IcmReportIcon,
  ManageAccounts as AccountActivityIcon,
  SupportAgent as FedSnowReportIcon,
  BarChart as AnalyticsIcon,
  SupervisorAccount as ManageRequestsIcon,
  PeopleAlt as OnboardingBuddyIcon,
  BadgeOutlined as GuestPassIcon,
  PersonOff as InactiveUsersIcon,
  PendingActions as TriageAgingIcon,
  SpaceDashboard as ExecutiveOverviewIcon,
  FilterAlt as IntakeFunnelIcon,
  TrendingUp as TemplateAdoptionIcon,
  MonitorHeart as PainMeterIcon,
  Medication as ReliefEffectivenessIcon,
  DonutLarge as OutcomeMixIcon,
  Speed as ApprovalPerformanceIcon,
  ThumbDownAlt as RejectionReasonsIcon,
  SyncProblem as WorkflowHealthIcon,
  EventBusy as ExceptionExpiryIcon,
  FactCheck as DataQualityIcon,
  AltRoute as RoutingReportIcon,
  GavelOutlined as DeclinedDetailIcon,
  CallSplit as DeflectionReportIcon,
  BubbleChart as HotSpotsIcon,
  ShieldOutlined as ElevatedAccessIcon,
  QueryStats as TriageLagTrendIcon,
  HourglassBottom as TriageTimingIcon,
  Inventory2 as WorkloadReportingIcon,
  DashboardCustomize as WorkloadSummaryIcon,
  Bolt as EfficiencyIcon,
  AssignmentTurnedIn as IntakeReportingIcon,
  PlaylistAddCheck as KrCountsIcon,
  Anchor as IpAnchorIcon,
  Lan as IpAnchorAdminIcon,
} from '@mui/icons-material';
import type { ToolGroup, ToolTile } from './ToolTileGrid';
import { isFeatureEnabled } from '../featureFlags';
import { canReadApprovalBottlenecks, canReadApprovalDecisionHistory } from '../utils/approvalDecisionHistory';
import IamAgentLogo from './IamAgentLogo';

/**
 * The tiles behind Administration → Admin Tools and Administration → Reports.
 *
 * Kept out of the pages so the sidebar can gate its entries on the same definitions the pages
 * render: a nav entry is shown exactly when the page it opens has something on it.
 *
 * Declaration order does not matter — groups and tiles are sorted by label at render.
 */

const isAdmin = (r: string) => r === 'AskIAM.Admin';

/** Shared gate for the request/exception management surfaces. */
export const canManageRequests = (roles: string[]) =>
  roles.some((r) => (r.startsWith('AskIAM.Requests.') && r.endsWith('.Manage')) || isAdmin(r));

const canManageWorkItems = (roles: string[]) =>
  roles.some((r) => r === 'AskIAM.WorkItem.Manage' || isAdmin(r));

/** IP Anchor owner surface: any IP Anchor role (server policy IpAnchorRead), admitting AskIAM.Admin. */
const canReadIpAnchor = (roles: string[]) =>
  roles.some(
    (r) => r === 'AskIAM.IpAnchor.Read' || r === 'AskIAM.IpAnchor.Write' || r === 'AskIAM.IpAnchor.Admin' || isAdmin(r),
  );

/** IP Anchor admin surface (server policy IpAnchorAdmin), admitting AskIAM.Admin. */
const canAdministerIpAnchor = (roles: string[]) => roles.some((r) => r === 'AskIAM.IpAnchor.Admin' || isAdmin(r));

export const adminToolGroups: ToolGroup[] = [
  {
    label: 'Work Intake',
    tiles: [
      {
        label: 'Onboard Team',
        description:
          'Register a ServiceTree-backed team in the work-item system: confirm owners and configure destinations. Routing and custom fields are managed centrally in Routing Administration.',
        path: '/work-items/onboard',
        icon: <WorkItemsIcon sx={{ fontSize: 36 }} />,
        requireRole: canManageWorkItems,
      },
      {
        label: 'Routing Administration',
        description:
          'Author the Technology → Product taxonomy, manage environments, assign teams via the Product × Environment routing table, and define product custom fields.',
        path: '/work-items/routing',
        icon: <WorkItemsIcon sx={{ fontSize: 36 }} />,
        requireRole: canManageWorkItems,
      },
      {
        label: 'Team Capability Matrix',
        description:
          'Map team roles (Owner, Delegate, PM, Member) to capabilities. One global model applied to every onboarded team, with an append-only revision history and rollback.',
        path: '/work-items/capability-matrix',
        icon: <WorkItemsIcon sx={{ fontSize: 36 }} />,
        requireRole: canManageWorkItems,
      },
      {
        label: 'Deflection Catalog',
        description:
          'Manage preliminary-review tool, process, and support deflections shown before engineering work request submission.',
        path: '/work-items/deflection-catalog',
        icon: <WorkItemsIcon sx={{ fontSize: 36 }} />,
        requireRole: canManageWorkItems,
      },
      {
        label: 'Deflection Taxonomy Mapping',
        description:
          'Bulk-map deflection catalog entries to the governed Technology and Product taxonomy used by Report Central.',
        path: '/work-items/taxonomy-mapping',
        icon: <WorkItemsIcon sx={{ fontSize: 36 }} />,
        requireRole: canManageWorkItems,
        requireFeature: 'Intake.TaxonomyMapping',
      },
      {
        label: 'Manage Team',
        description:
          'Edit an onboarded work-item team: owners, delegates, destinations, and the routes that point to it.',
        path: '/work-items/teams',
        icon: <WorkItemsIcon sx={{ fontSize: 36 }} />,
        requireRole: canManageWorkItems,
      },
    ],
  },
  {
    label: 'Automation',
    tiles: [
      {
        label: 'Harvest Management',
        description: 'Monitor and manage Graph data ingestion pipelines and schedules.',
        path: '/harvest',
        icon: <HarvestIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) => roles.some((r) => r === 'AskIAM.Harvest.Manage' || isAdmin(r)),
        requireFeature: 'IdentityTools.Harvest',
      },
      {
        label: 'PIM Approver Sync',
        description: 'Configure and monitor PIM role-to-approver group synchronization.',
        path: '/pim-sync',
        icon: <PimSyncIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) => roles.some((r) => r === 'AskIAM.PimSync.Manage' || isAdmin(r)),
        requireFeature: 'IdentityTools.PimSync.Management',
      },
    ],
  },
  {
    label: 'Configuration Management',
    tiles: [
      {
        label: 'Clearance',
        description: 'Manage Federated Identity Credentials, operation contexts, and service parameters.',
        path: '/clearance',
        icon: <ClearanceIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) => roles.some((r) => r === 'AskIAM.Clearance.Read' || isAdmin(r)),
        requireFeature: 'IdentityTools.Clearance',
      },
      {
        label: 'FIC Diagnostics',
        description: 'Diagnose Federated Identity Credential configurations and token flows.',
        path: '/admin/diagnostics',
        icon: <DiagnosticsIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) => roles.some(isAdmin),
      },
    ],
  },
  {
    label: 'Operations',
    tiles: [
      {
        label: 'Operations Center',
        description:
          'Monitor approval workflow health and diagnose or recover MSApprovals issues. Approval analytics live in Reports.',
        path: '/admin/operations-center',
        icon: <ApprovalIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) =>
          roles.some((r) => isAdmin(r) || r === 'AskIAM.MsApprove.Admin') || canManageRequests(roles),
        requireFeature: 'IdentityTools.MsApproveOperations',
      },
      {
        label: 'Change Impact Radar',
        description:
          'Pre-change baselining, post-change monitoring, and go/no-go decisioning for identity deployments.',
        path: '/change-radar',
        icon: <ChangeRadarIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) =>
          roles.some((r) => r === 'AskIAM.ChangeRadar.Read' || r === 'AskIAM.ChangeRadar.Manage' || isAdmin(r)),
        requireFeature: 'IdentityTools.ChangeRadar',
        isPreview: (f) => f.changeRadarPreview,
      },
      {
        label: 'Conditional Access Readiness',
        description:
          'Review live TSE or Corp sign-in health, Conditional Access failure categories, and ownership routing.',
        path: '/conditional-access-readiness',
        icon: <ConditionalAccessReadinessIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) => roles.some(isAdmin),
        requireFeature: 'IdentityTools.ConditionalAccessReadiness',
      },
      {
        label: 'CA Incident Investigation',
        description:
          'Investigate TSE or Corp sign-in evidence for secure Conditional Access diagnosis.',
        path: '/conditional-access-investigation',
        icon: <ConditionalAccessInvestigationIcon sx={{ fontSize: 36 }} />,
        requireRole: (roles) => roles.some(isAdmin),
        requireFeature: 'IdentityTools.ConditionalAccessInvestigation',
      },
      {
        label: 'Partner Governance',
        description:
          'Review cross-tenant access partner settings and external collaboration posture across all configured partner tenants.',
        path: '/partner-governance',
        icon: <PartnerGovernanceIcon sx={{ fontSize: 36 }} />,
        requireFeature: 'IdentityTools.PartnerGovernance',
        requireRole: (roles) => roles.some(isAdmin),
      },
      {
        label: 'IP Anchor Admin',
        description:
          'Review service-account eligibility, shared Azure egress and named-location capacity, and load analysis snapshots.',
        path: '/ipanchor/admin',
        icon: <IpAnchorAdminIcon sx={{ fontSize: 36 }} />,
        requireRole: canAdministerIpAnchor,
        requireFeature: 'IdentityTools.IpAnchor',
      },
      // CoreID HR Lookup remains reachable by direct URL, but is intentionally navigation-hidden.
    ],
  },
];

/** Every admin tool, flattened — for the sidebar's Admin Tools visibility gate. */
export const allAdminTools: ToolTile[] = adminToolGroups.flatMap((g) => g.tiles);

export const managementTools: ToolTile[] = [
  {
    label: 'Manage Requests',
    description: 'Work the queue of submitted access requests: review, approve, deny, and track them to completion.',
    path: '/manage-requests',
    icon: <ManageRequestsIcon sx={{ fontSize: 36 }} />,
    requireRole: canManageRequests,
  },
];

/**
 * The tiles behind User Tools.
 *
 * Declared here rather than in `UserToolsPage` for the same reason as the admin and report tiles:
 * the sidebar gates its "User Tools" entry on this list, so a tool gated off here cannot leave a
 * nav entry pointing at an empty page.
 */
export const userTools: ToolTile[] = [
  {
    label: 'Onboarding Buddy',
    description: 'View your onboarding status, team info, and group memberships.',
    path: '/onboarding-buddy',
    icon: <OnboardingBuddyIcon sx={{ fontSize: 36 }} />,
    requireFeature: 'IdentityTools.OnboardingBuddy',
  },
  {
    label: 'Guest Pass',
    description:
      'Manage guests you sponsor — reset MFA, enable, disable, delete, and transfer sponsorship — with approvals routed automatically.',
    path: '/guestpass',
    icon: <GuestPassIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) =>
      roles.some((r) => r === 'AskIAM.GuestPass.Read' || r === 'AskIAM.GuestPass.Write' || isAdmin(r)),
  },
  // 'HR Account Lookup' is intentionally not listed as a User Tools tile — the page remains
  // reachable at /hr-account-lookup (server-side HrAccountLookupAuthorization.IsAllowed still
  // gates it), but it is navigation-hidden: direct URL only, not surfaced via the UI.
  {
    label: 'Inactive Users',
    description:
      'Review inactive accounts across tenants — last sign-in, enabled/deleted state — with filtering and CSV export.',
    path: '/inactive-users',
    icon: <InactiveUsersIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.InactiveUsers.Read' || isAdmin(r)),
    requireFeature: 'Reports.InactiveUsers',
  },
  {
    label: 'IP Anchor',
    description: 'Anchor the service accounts you sponsor to the outbound IP addresses your team owns.',
    path: '/ipanchor',
    icon: <IpAnchorIcon sx={{ fontSize: 36 }} />,
    requireRole: canReadIpAnchor,
    requireFeature: 'IdentityTools.IpAnchor',
  },
  {
    label: 'IAM Agent Assistant',
    description: 'Investigate IAM problems across the accounts and Entra applications you own.',
    path: '/iam-agent-assistant',
    icon: <IamAgentLogo sx={{ fontSize: 36 }} />,
    requireFeature: 'Operations.SreAgentRead',
  },
];

/**
 * Outcome-based grouping for the report catalog. The flat report grid grew past two dozen tiles, so
 * Reports now leads with a small set of parent buckets and each report is a sub-tile under one.
 *
 * This is cosmetic navigation only. A bucket is not an authorization boundary: every report keeps
 * its own `requireRole`/`requireFeature` gate (below) and its own route + backend endpoint policy,
 * so a parent tile is shown purely as the union of its visible children, and deep-linking a report
 * a user is not entitled to still fails closed at the route and the API exactly as before. Reports
 * are partitioned across buckets by the compiler: {@link ReportTile} makes `reportBucket` required,
 * so a new report cannot be added without placing it in a bucket.
 */
export type ReportBucketKey = 'workload' | 'efficiency' | 'exceptions';

export type ReportTile = ToolTile & { reportBucket: ReportBucketKey };

export interface ReportBucket {
  key: ReportBucketKey;
  label: string;
  description: string;
  icon: React.ReactNode;
}

export const reportBuckets: ReportBucket[] = [
  {
    key: 'workload',
    label: 'Workload Reporting',
    description:
      'The volume of intake work accepted across AskIAM, ICM, and routed requests — and the automation and triage effort carrying it.',
    icon: <WorkloadReportingIcon sx={{ fontSize: 36 }} />,
  },
  {
    key: 'efficiency',
    label: 'AskIAM Efficiency',
    description:
      'How well the portal deflects and resolves: template adoption and cold tiles, deflection, outcomes, rejections, and data quality.',
    icon: <EfficiencyIcon sx={{ fontSize: 36 }} />,
  },
  {
    key: 'exceptions',
    label: 'Exceptions & Expiry',
    description: 'Exception requests, their approval state, and the expiry pipeline for granted exceptions.',
    icon: <ExceptionReportIcon sx={{ fontSize: 36 }} />,
  },
];

/** The reports belonging to one bucket, in the catalog's label order. */
export const reportTilesForBucket = (key: ReportBucketKey): ReportTile[] =>
  reportTiles.filter((t) => t.reportBucket === key);

export const reportTiles: ReportTile[] = [
  {
    label: 'Triage Lag Trend',
    reportBucket: 'efficiency',
    description: 'Business-day lag signals for closed intake items over time, split by the team that held the item when it resolved.',
    path: '/reports/triage-lag',
    icon: <TriageLagTrendIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.TriageLagTrend',
  },
  {
    label: 'Triage Timing',
    reportBucket: 'efficiency',
    description: 'E3/E4: queue time before the first triage touch, deliberation time after it, and the business days each reroute hop costs.',
    path: '/reports/triage-timing',
    icon: <TriageTimingIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.TriageTiming',
  },
  {
    label: 'Report Central Overview',
    reportBucket: 'efficiency',
    description: 'Manager-ready service health: intake, closures, triage lag, deflection, reroute rate, and emerging hot spots.',
    path: '/reports/overview',
    icon: <ExecutiveOverviewIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.ExecutiveOverview',
  },
  {
    label: 'Triage Aging',
    reportBucket: 'efficiency',
    description: 'Open intake items awaiting a team triage decision, with business-day age bands, SLA breaches, and the oldest queue tail.',
    path: '/reports/triage-aging',
    icon: <TriageAgingIcon sx={{ fontSize: 36 }} />,
    requireFeature: 'Reports.TriageAging',
    // Strict `granted`, not `isVisible`. Every other report tile carries a requireRole, so this is
    // the only one whose visibility rests entirely on team access — which makes it the single tile
    // that can surface the Reports nav entry, and with it the whole Administration section, to a
    // user who holds no role at all. Deliberately not given a requireRole instead: that would take
    // this report away from the triagers it is for, who hold no Reports.* role. Fail-open is
    // acceptable for an entry whose 503 explains itself; it is not acceptable for one that reveals
    // an unrelated section.
    requireTeamAccess: (access) => access.canTriage === 'granted',
  },
  {
    label: 'Intake Funnel',
    reportBucket: 'efficiency',
    description: 'Of everything that arrived at intake, what happened to it: pre-submission deflection followed by triage outcomes, org-wide.',
    path: '/reports/intake-funnel',
    icon: <IntakeFunnelIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.IntakeFunnel',
  },
  {
    label: 'Hot Spots',
    reportBucket: 'efficiency',
    description: 'The Report Central headline: Technology × Product heat map, environment pressure, and owning-team load from the same filters.',
    path: '/reports/hot-spots',
    icon: <HotSpotsIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.HotSpots',
  },
  {
    label: 'Cross-Surface Hot Spots',
    reportBucket: 'efficiency',
    description: 'Compare Request Central submissions and routed intake work requests on the shared taxonomy.',
    path: '/reports/cross-surface-hot-spots',
    icon: <HotSpotsIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.CrossSurfaceHotSpots',
  },
  {
    label: 'Template Adoption',
    reportBucket: 'efficiency',
    description: 'Which request and exception templates are actually used, the cold ones worth retiring, and usage cross-tabbed against catalog state.',
    path: '/reports/template-adoption',
    icon: <TemplateAdoptionIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Adoption.Read' || isAdmin(r)),
    requireFeature: 'Reports.TemplateAdoption',
  },
  {
    label: 'Routing Accuracy',
    reportBucket: 'efficiency',
    description: 'Reroute rate, hop distribution, fixable Product × Environment routing defects, and decline-reason trends.',
    path: '/reports/routing',
    icon: <RoutingReportIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Routing.Read' || isAdmin(r)),
    requireFeature: 'Reports.Routing',
  },
  {
    label: 'Denied & Redirected Detail',
    reportBucket: 'efficiency',
    description: 'The rows behind the decline numbers: every request turned away, the reason it was given, and where a redirect pointed instead.',
    path: '/reports/declined-detail',
    icon: <DeclinedDetailIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.DeclinedDetail',
  },
  {
    label: 'Deflection Effectiveness',
    reportBucket: 'efficiency',
    description: 'What self-service was offered, what submitters followed, and which targets are actually saving engineering work.',
    path: '/reports/deflection',
    icon: <DeflectionReportIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.DeflectionEffectiveness',
  },
  {
    label: 'Exception Report',
    reportBucket: 'exceptions',
    description: 'Review exception requests and their approval state across the environments you manage.',
    path: '/exception-report',
    icon: <ExceptionReportIcon sx={{ fontSize: 36 }} />,
    requireRole: canManageRequests,
    requireFeature: 'Reports.Exceptions',
  },
  {
    label: 'Summary',
    reportBucket: 'workload',
    description: 'All-up current ICM, Intake, and FORGE-native KR workload with FORGE hierarchy drill-down.',
    path: '/reports/workload-summary',
    icon: <WorkloadSummaryIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.IcmReport.Read' || isAdmin(r)),
    requireFeature: 'Reports.KrCounts',
  },
  {
    label: 'ICM',
    reportBucket: 'workload',
    description: 'Incident volume, trends, and queue breakdowns for the IcM services you support.',
    path: '/icm-report',
    icon: <IcmReportIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.IcmReport.Read' || isAdmin(r)),
    requireFeature: 'Reports.Icm',
  },
  {
    label: 'Intake',
    reportBucket: 'workload',
    description: 'Request Engineering Work accepted directly in AskIAM, grouped by onboarded FORGE hierarchy.',
    path: '/reports/intake',
    icon: <IntakeReportingIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.IcmReport.Read' || isAdmin(r)),
    requireFeature: 'Reports.KrCounts',
  },
  {
    label: 'KRs',
    reportBucket: 'workload',
    description: 'Active FORGE-native KRs that did not originate from AskIAM Request Engineering Work.',
    path: '/reports/kr-counts',
    icon: <KrCountsIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.IcmReport.Read' || isAdmin(r)),
    requireFeature: 'Reports.KrCounts',
  },
  {
    label: 'Account Activity',
    reportBucket: 'efficiency',
    description: 'Compare successful on-premises and Entra authentication evidence for AD-backed user and service accounts.',
    path: '/reports/account-activity',
    icon: <AccountActivityIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.AccountActivity.Read' || isAdmin(r)),
    requireFeature: 'Reports.AccountActivity',
  },
  {
    label: 'FedServiceNow Open Incidents',
    reportBucket: 'efficiency',
    description: 'Current active, non-resolved Fed-AAD Engineering ServiceNow incidents, with assignment and state drill-down.',
    path: '/reports/fed-snow',
    icon: <FedSnowReportIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.FedSnow.Read' || isAdmin(r)),
    requireFeature: 'Reports.FedSnow',
  },
  {
    label: 'Elevated Access Insights',
    reportBucket: 'efficiency',
    description: 'Normalized elevated-access request cohort, approval/completion rates, and fulfillment throughput from AYGA.',
    path: '/reports/elevated-access',
    icon: <ElevatedAccessIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.ElevatedAccess.Read' || isAdmin(r)),
    requireFeature: 'Reports.ElevatedAccess',
  },
  {
    label: 'JMM Pain Meter',
    reportBucket: 'efficiency',
    description: 'A doctor-office 1–10 meter of each team\u2019s weighted workload (raw "pain") and the automation that medicates it, grouped by the same hierarchy as the ICM report. Prototype.',
    path: '/reports/pain-meter',
    icon: <PainMeterIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.PainMeter.Read' || isAdmin(r)),
    requireFeature: 'Reports.PainMeter',
  },
  {
    label: 'Relief Effectiveness',
    reportBucket: 'efficiency',
    description:
      'Trends each Pain-Meter relief source against the outcome it should move (TTM, TTR, inflow, handling time) with a before/after verdict around each intervention date. Answers whether our medicated relief is actually working. Prototype.',
    path: '/reports/relief-effectiveness',
    icon: <ReliefEffectivenessIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.ReliefEffectiveness.Read' || isAdmin(r)),
    requireFeature: 'Reports.ReliefEffectiveness',
  },
  {
    label: 'Outcome Mix',
    reportBucket: 'efficiency',
    description: 'Compare how each request and exception template resolves — normalized outcome shares with the raw denominator kept visible.',
    path: '/reports/outcome-mix',
    icon: <OutcomeMixIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Adoption.Read' || isAdmin(r)),
    requireFeature: 'Reports.OutcomeMix',
  },
  {
    label: 'Approval Performance',
    reportBucket: 'efficiency',
    description: 'Find approval bottlenecks and inspect scoped decision history, trends and CSV exports.',
    path: '/reports/approval-performance',
    icon: <ApprovalPerformanceIcon sx={{ fontSize: 36 }} />,
    requireAccess: ({ roles, flags }) => canReadApprovalBottlenecks(roles) ||
      (isFeatureEnabled(flags, 'IdentityTools.MsApproveOperations') && canReadApprovalDecisionHistory(roles)),
    requireFeature: 'Reports.ApprovalPerformance',
  },
  {
    label: 'Rejection Reasons',
    reportBucket: 'efficiency',
    description: 'Why requests get rejected, by template — reason-code concentration, with every uncoded rejection counted rather than hidden.',
    path: '/reports/rejection-reasons',
    icon: <RejectionReasonsIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Adoption.Read' || isAdmin(r)),
    requireFeature: 'Reports.RejectionReasons',
  },
  {
    label: 'Workflow Failure Health',
    reportBucket: 'efficiency',
    description: 'Failure codes and hard-failure rates by template, separating downstream HTTP breaks from AskIAM template/config issues when the run evidence supports it.',
    path: '/reports/workflow-health',
    icon: <WorkflowHealthIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Adoption.Read' || isAdmin(r)),
    requireFeature: 'Reports.WorkflowHealth',
  },
  {
    label: 'Exception Expiry',
    reportBucket: 'exceptions',
    description: 'Track exception expiry as a point-in-time pipeline: active, exclusive 30/60/90-day windows, expired, revoked, and missing dates.',
    path: '/reports/exception-expiry',
    icon: <ExceptionExpiryIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Adoption.Read' || isAdmin(r)),
    requireFeature: 'Reports.ExceptionExpiry',
  },
  {
    label: 'Usage Analytics',
    reportBucket: 'efficiency',
    description: 'Portal usage over time: page views, active users, and the surfaces people actually reach for.',
    path: '/analytics',
    icon: <AnalyticsIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Analytics.Read' || isAdmin(r)),
    requireFeature: 'Reports.Analytics',
  },
  {
    label: 'Reporting Data Quality',
    reportBucket: 'efficiency',
    description: 'Can you believe the numbers? Unmapped statuses, missing taxonomy, dangling references, and report exclusions.',
    path: '/reports/data-quality',
    icon: <DataQualityIcon sx={{ fontSize: 36 }} />,
    requireRole: (roles) => roles.some((r) => r === 'AskIAM.Reports.Intake.Read' || isAdmin(r)),
    requireFeature: 'Reports.DataQuality',
  },
];
